import type { Params } from "@solidjs/router";
import { useChatClient } from "@storytell/cp-client";
import { createConversationRequest, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { ConversationResponse } from "@storytell/cp-client/src/gen/chat/v3/chat_pb";
import type { StreamingChatId, StreamingService } from "@storytell/streaming";
import { createMemo, createResource } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { createResourceStorage } from "~/lib/createResourceStorage";
import type { ChatServiceState, SetChatServiceState } from "./chatService";

export const createChatServiceResource = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  urlParams: () => Params;
  setChatTitle?: (chatId: StreamingChatId, title: string) => void;
  state: ChatServiceState;
  setState: SetChatServiceState;
  stream: StreamingService;
}) => {
  const chatClient = useChatClient();

  const canFetch = () => {
    if (isServer) return undefined;

    const chatId = deps.urlParams().chatId;
    if (!deps.authReady()) return undefined;
    return chatId;
  };

  const storage = createResourceStorage(
    () => undefined as unknown,
    (data: unknown) => {
      if (!data) return;
      const d = data as unknown as
        | { type: "success"; data: ConversationResponse }
        | { type: "error"; error: unknown; chatId: StreamingChatId };
      if (d.type === "success") {
        deps.stream.chat.processChatRecording(d.data);
        if (deps.setChatTitle && d.data.title) {
          deps.setChatTitle(d.data.chatId as StreamingChatId, d.data.title);
        }
      } else if (d.type === "error") {
        console.error("chatService: error fetching chat:", { error: d.error });
        deps.setState({
          isStreaming: false,
          chatId: d.chatId,
          error: d.error as Error,
        });
      }
    },
  );

  const [resource] = createResource(
    canFetch,
    async (chatId) => {
      try {
        // If we're streaming, we don't need to fetch the chat
        if (deps.state.isStreaming) return;

        if (!chatId) {
          console.log("chatService: chatId is undefined, skipping resource fetch");
          deps.setState({
            isStreaming: false,
            chatId: undefined,
            error: undefined,
          });
          return;
        }
        deps.setState("chatId", chatId as StreamingChatId);

        const conversation = await chatClient.conversation(
          createConversationRequest({
            chatId: chatId,
            recentMessagesCount: BigInt(20),
          }),
          useCallOptions(withBearerToken(deps.authToken())),
        );

        if (conversation.isOk()) {
          const data = conversation.value;
          if (isServer) {
            storage[1]({ type: "success", data });
          }
          return { type: "success", data } as const;
        }

        console.error("chatService: error fetching chat:", { 
          chatId, 
          error: conversation.error,
          errorCode: conversation.error.code,
          errorMessage: conversation.error.message,
          errorDetail: conversation.error.detail,
        });
        // Convert ErrorResult to Error object for storage
        const errorObj = new Error(
          conversation.error.message || conversation.error.detail || String(conversation.error.code || "Unknown error"),
        );
        // Store the original error result on the error object for reference
        (errorObj as Error & { errorResult?: typeof conversation.error }).errorResult = conversation.error;
        
        const errorResult = { type: "error" as const, error: conversation.error, chatId };
        // Set error state immediately on client, not just on server
        if (!isServer) {
          deps.setState({
            isStreaming: false,
            chatId: chatId as StreamingChatId,
            error: errorObj,
          });
        }
        if (isServer) {
          storage[1](errorResult);
        }
        return errorResult;
      } catch (error) {
        console.error("chatService: error fetching chat:", { chatId, error });
        const errorResult = { type: "error" as const, error, chatId };
        // Set error state immediately on client, not just on server
        if (!isServer) {
          deps.setState({
            isStreaming: false,
            chatId: chatId as StreamingChatId,
            error: error as Error,
          });
        }
        if (isServer) {
          storage[1](errorResult);
        }
        return errorResult;
      }
    },
    {
      deferStream: true,
      //@ts-expect-error
      storage: () => storage,
    },
  );

  return resource;
};
